import { readUIMessageStream } from 'ai'

import type { NoahMessage, UserMessageMetadata } from '../types'
import type { UIMessageChunk } from 'ai'

/**
 * Fold the persisted UI message stream from `GET /api/sessions/{id}`
 * (`SessionEventsResponse.events`) back into the `UIMessage[]` shape that
 * `useChat({ messages })` consumes.
 *
 * Backend contract (rehydration only — live `/api/chat` never emits user
 * chunks):
 *
 *  - Each persisted assistant turn is a `start` … `finish` segment in the
 *    exact shape the live stream uses, so `readUIMessageStream` assembles
 *    the parts unchanged.
 *  - Before each segment, the backend emits `data-user-message` chunks for
 *    EVERY prior user prompt cumulatively, ending with the new prompt that
 *    triggered the upcoming segment. So a 3-turn history looks like:
 *       U1, start(A1)…finish, U1, U2, start(A2)…finish, U1, U2, U3, start(A3)…
 *  - `data-user-message` chunks carry a stable `id` on the chunk envelope
 *    (e.g. `afKH77IjU8AkIQC5`) used for dedup across replays.
 *
 * Strategy: walk events left-to-right. Track which user-message ids have
 * been emitted; emit each one only at its first occurrence. Accumulate
 * everything between `start` markers into a segment, then drain through
 * `readUIMessageStream` to get the assistant `UIMessage`.
 */
export async function assembleSessionEvents(
	events: Array<UIMessageChunk>,
): Promise<Array<NoahMessage>> {
	const items = partition(events)

	const out: Array<NoahMessage> = []
	let segmentIndex = 0
	let userFallbackIndex = 0

	for (const item of items) {
		if (item.kind === 'user') {
			out.push({
				id: item.id ?? `user-${userFallbackIndex++}`,
				role: 'user',
				parts: [{ type: 'text', text: item.text }],
				...(item.metadata !== undefined && { metadata: item.metadata }),
			} as NoahMessage)
			continue
		}

		const message = await drainSegment(item.events)
		const index = segmentIndex++
		if (!message) continue

		// Defensive: if a legacy or unexpected stream embeds `data-user-message`
		// chunks INSIDE a segment, strip them from the assistant's parts.
		// Live backend doesn't do this anymore, but historical fixtures did.
		const filteredParts = message.parts.filter(
			(p) => (p as { type?: string }).type !== 'data-user-message',
		)
		const safeId =
			typeof message.id === 'string' && message.id.length > 0
				? message.id
				: `assistant-${index}`

		out.push({
			...message,
			id: safeId,
			parts: filteredParts,
		} as NoahMessage)
	}

	return out
}

// ─── Partitioning ────────────────────────────────────────────────────────────

type PartitionedItem =
	| {
			kind: 'user'
			id: string | undefined
			text: string
			metadata: UserMessageMetadata | undefined
	  }
	| { kind: 'segment'; events: Array<UIMessageChunk> }

type ChunkWithId = {
	id?: string
	data?: { text?: unknown; metadata?: unknown }
}

function partition(events: Array<UIMessageChunk>): Array<PartitionedItem> {
	const items: Array<PartitionedItem> = []
	const seenUserKeys = new Set<string>()
	let segment: Array<UIMessageChunk> | undefined

	const flushSegment = () => {
		if (segment && segment.length > 0) {
			items.push({ kind: 'segment', events: segment })
		}
		segment = undefined
	}

	for (const event of events) {
		if (event.type === 'data-user-message') {
			// User chunks always live BETWEEN segments. If a stream somehow
			// interleaves one inside a segment, close the segment first so the
			// resulting message ordering still matches `useChat`'s expectations.
			flushSegment()

			const envelope = event as unknown as ChunkWithId
			const text = envelope.data?.text
			if (typeof text !== 'string') continue

			// Dedupe by chunk id when present; otherwise fall back to text so
			// id-less replays don't double-emit. Text is a safe fallback because
			// the same user only sends the same prompt verbatim in pathological
			// cases — and even then the duplicate would be a true duplicate.
			const dedupeKey = envelope.id ?? `text:${text}`
			if (seenUserKeys.has(dedupeKey)) continue
			seenUserKeys.add(dedupeKey)

			const metadata = parseUserMetadata(envelope.data?.metadata)
			items.push({ kind: 'user', id: envelope.id, text, metadata })
			continue
		}

		if (event.type === 'start') {
			flushSegment()
			segment = [event]
			continue
		}

		if (segment) {
			segment.push(event)
		}
		// Events before the first `start` (other than `data-user-message`)
		// have no segment to attach to — silently drop.
	}

	flushSegment()
	return items
}

// ─── Metadata parsing ────────────────────────────────────────────────────────

/**
 * Best-effort coerce a raw `data-user-message.data.metadata` value into our
 * `UserMessageMetadata` shape. The backend ships a loose dict (`tenant_id` +
 * `page_context` raw) so we accept anything object-shaped and narrow what we
 * recognize. Unknown `page_type` values pass through — the pill component
 * filters `unsupported` at render time.
 */
function parseUserMetadata(raw: unknown): UserMessageMetadata | undefined {
	if (!raw || typeof raw !== 'object') return undefined
	const obj = raw as Record<string, unknown>
	const out: UserMessageMetadata = {}
	if (typeof obj.tenant_id === 'string') out.tenant_id = obj.tenant_id
	if (
		obj.page_context &&
		typeof obj.page_context === 'object' &&
		typeof (obj.page_context as Record<string, unknown>).page_type === 'string'
	) {
		out.page_context = obj.page_context as UserMessageMetadata['page_context']
	}
	if (out.tenant_id === undefined && out.page_context === undefined)
		return undefined
	return out
}

async function drainSegment(
	segment: Array<UIMessageChunk>,
): Promise<NoahMessage | undefined> {
	const stream = new ReadableStream<UIMessageChunk>({
		start(controller) {
			for (const chunk of segment) controller.enqueue(chunk)
			controller.close()
		},
	})

	let latest: NoahMessage | undefined
	// `terminateOnError: false` lets a partially-truncated history (e.g. no
	// closing `finish` because the original stream was aborted mid-response)
	// still yield the partial state instead of throwing.
	for await (const message of readUIMessageStream<NoahMessage>({
		stream,
		terminateOnError: false,
	})) {
		latest = message
	}
	return latest
}
